import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import getNavigation from "../../navigation/navigation.config";
import {
  Box,
  CardMedia,
  Drawer,
  List,
  Typography,
  useTheme,
  Stack,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { EGIconButton } from "../../../components/MainComponents/Button/IconButton/IconButton";
import { SidebarItem } from "./SidebarItem/SidebarItem";
import { SidebarGroup } from "./SidebarGroup/SidebarGroup";
import SidebarExternal from "./SidebarExternal/SidebarExternal";
import EGLogo from "../../../assets/MainLogo/Imagotipo_EG.svg";

const Sidebar = ({
  open,
  onToggleSidebar,
  isMobile,
  setShowSidebar,
  customFilters,
}) => {
  // Custom hooks
  const { t } = useTranslation("components");
  const theme = useTheme();
  const navigationItems = getNavigation(t, customFilters);
  const location = useLocation();
  // Initial States
  // Extend/collapse button hovered iconButton style
  const [openHovered, setOpenHovered] = useState(false);
  // Shows the menu items (open sidebar)
  const [expandedGroups, setExpandedGroups] = useState({});
  // Show menu grups (close sidebar)
  const [openMenu, setOpenMenu] = useState(null);
  // Auxiliary state for rendering at collapse
  const [contentVisible, setContentVisible] = useState(open);

  // Sidebar width
  const drawerWidth = 199;
  const collapsedWidth = 70;

  // Auxiliary to render elements correctly when expanding sidebars
  useEffect(() => {
    let timeout;
    if (open) {
      timeout = setTimeout(() => {
        setContentVisible(true);
      }, 10);
    } else {
      setContentVisible(false);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [open]);

  // Show selected menu
  const showMenu = (key) => openMenu === key;
  // Open/close selected menu
  const toggleGroup = (segment, level) => {
    setExpandedGroups((prev) => {
      const key = `level${level}`;
      return {
        ...prev,
        [key]: prev[key] === segment ? null : segment,
      };
    });
  };

  // Determines which item is active
  const isActive = (segment, activeMatch = []) => {
    const path = location.pathname;
    if (activeMatch.length) return activeMatch.some((m) => path.includes(m));
    return path.includes(segment);
  };

  //  Determine the icon to be displayed according to the status
  const getIconName = () => {
    if (open) return "outline_sidebar_left";
    if (openHovered) return "outline_sidebar_right";
    return "outline_password_check";
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? true : open}
      onClose={isMobile ? () => setShowSidebar(false) : undefined}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: 20,
          }),
        "& .MuiDrawer-paper": {
          ...(isMobile && {
            borderTopRightRadius: 8,
            borderTopLeftRadius: 0,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 0,
          }),
          width: open ? drawerWidth : collapsedWidth,
          pt: isMobile ? 1.5 : 6.4,
          overflowX: "hidden",
          overflow: "visible",
          boxShadow: "none",
          borderRight: "none",
          border: "none",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: 20,
            }),
        },
      }}
    >
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <List
          component="nav"
          disablePadding
          sx={{
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            flexGrow: 1,
          }}
        >
          {/* Header button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "end" : "center",
              mr: isMobile ? 0 : open ? 0.6 : 0,
            }}
          >
            {isMobile ? (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                  mx: "auto",
                  px: 0.5,
                }}
                spacing={1}
              >
                <CardMedia
                  component="img"
                  image={EGLogo}
                  sx={{ mx: "auto", my: 0.5, maxHeight: 35.5, maxWidth: 170 }}
                  alt="Logo"
                />
              </Stack>
            ) : (
              <EGIconButton
                iconSize={20}
                onClick={onToggleSidebar}
                onMouseEnter={() => setOpenHovered(true)}
                onMouseLeave={() => setOpenHovered(false)}
                iconName={getIconName()}
              />
            )}
          </Box>
          {/* Navigation menu */}
          {navigationItems
            .filter((item) => item.kind !== "external")
            .map((item, i) => {
              switch (item.kind) {
                case "item":
                  return (
                    <Box
                      key={item.segment}
                      sx={{
                        position: "relative",
                        width: "100%",
                        ...(isActive(item.segment) && {
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: open ? 1.9 : 3.5,
                            width: 4,
                            bgcolor: theme.palette.primary.main,
                            borderRadius: "0 4px 4px 0",
                            height: open ? 34 : 39.5,
                          },
                        }),
                      }}
                    >
                      <SidebarItem
                        item={item}
                        open={open}
                        contentVisible={contentVisible}
                        isActive={isActive}
                      />
                    </Box>
                  );
                case "group":
                  return (
                    <SidebarGroup
                      key={item.segment}
                      item={item}
                      open={open}
                      isActive={(segment, activeMatch) =>
                        isActive(segment, activeMatch)
                      }
                      expandedGroups={expandedGroups}
                      toggleGroup={toggleGroup}
                      contentVisible={contentVisible}
                      openMenu={openMenu}
                      setOpenMenu={setOpenMenu}
                      index={i}
                      showMenu={showMenu}
                      level={0}
                    />
                  );

                default:
                  return null;
              }
            })}
        </List>
      </Box>
      {/* External Items (Footer) */}
      <Box sx={{ mb: 2 }}>
        {navigationItems
          .filter((item) => item.kind === "external")
          .map((item, i) => (
            <SidebarExternal
              key={i}
              item={item}
              open={open}
              index={i}
              contentVisible={contentVisible}
            />
          ))}
        {open && contentVisible && (
          <Typography
            textAlign="center"
            color="primary"
            fontSize={10}
            fontWeight={600}
          >
            © Powered by EthicsGlobal®
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
